import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  author_name: '',
  rating: 5,
  text: '',
  avatar_url: '',
  is_active: true,
  display_order: 0,
};

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get(`${API}/testimonials/all`, { headers });
      setTestimonials(res.data);
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      toast.error('Error al cargar los testimonios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateDialog = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpenDialog(true);
  };

  const openEditDialog = (testimonial) => {
    setEditingId(testimonial.id);
    setForm({
      author_name: testimonial.author_name,
      rating: testimonial.rating,
      text: testimonial.text,
      avatar_url: testimonial.avatar_url || '',
      is_active: testimonial.is_active,
      display_order: testimonial.display_order,
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!form.author_name || !form.text) {
      toast.error('El nombre y el texto del testimonio son obligatorios');
      return;
    }

    try {
      const payload = { ...form, avatar_url: form.avatar_url || null };
      if (editingId) {
        await axios.put(`${API}/testimonials/${editingId}`, payload, { headers });
        toast.success('Testimonio actualizado correctamente');
      } else {
        await axios.post(`${API}/testimonials`, payload, { headers });
        toast.success('Testimonio creado correctamente');
      }
      setOpenDialog(false);
      fetchTestimonials();
    } catch (err) {
      console.error('Error saving testimonial:', err);
      toast.error('Error al guardar el testimonio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este testimonio? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/testimonials/${id}`, { headers });
      toast.success('Testimonio eliminado');
      fetchTestimonials();
    } catch (err) {
      toast.error('Error al eliminar el testimonio');
    }
  };

  const toggleActive = async (testimonial) => {
    try {
      await axios.put(
        `${API}/testimonials/${testimonial.id}`,
        {
          author_name: testimonial.author_name,
          rating: testimonial.rating,
          text: testimonial.text,
          avatar_url: testimonial.avatar_url,
          is_active: !testimonial.is_active,
          display_order: testimonial.display_order,
        },
        { headers }
      );
      fetchTestimonials();
    } catch (err) {
      toast.error('Error al cambiar el estado');
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-serif font-semibold text-primary" data-testid="admin-testimonials-title">
              Testimonios
            </h1>
            <p className="text-muted-foreground">
              Se muestran en la Home, en "¿Qué dicen nuestros clientes?"
            </p>
          </div>
          <Button onClick={openCreateDialog} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Testimonio
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="skeleton h-24 rounded-lg" />
            ))}
          </div>
        ) : testimonials.length > 0 ? (
          <div className="divide-y" data-testid="testimonials-list">
            {testimonials.map((testimonial, index) => (
              <div key={testimonial.id} className="p-6" data-testid={`testimonial-row-${index}`}>
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-primary">{testimonial.author_name}</h3>
                      <Badge className={testimonial.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                        {testimonial.is_active ? 'Visible' : 'Oculto'}
                      </Badge>
                      <span className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            fill={star <= testimonial.rating ? 'currentColor' : 'none'}
                            className={`w-3.5 h-3.5 ${star <= testimonial.rating ? 'text-accent' : 'text-slate-200'}`}
                          />
                        ))}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{testimonial.text}</p>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground">Visible</Label>
                      <Switch checked={testimonial.is_active} onCheckedChange={() => toggleActive(testimonial)} />
                    </div>
                    <Button variant="outline" size="icon" onClick={() => openEditDialog(testimonial)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => handleDelete(testimonial.id)}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            No hay testimonios todavía. Añade el primero con "Nuevo Testimonio".
          </div>
        )}
      </div>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar Testimonio' : 'Nuevo Testimonio'}</DialogTitle>
            <DialogDescription>
              Este contenido se muestra públicamente en la Home.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="author_name">Nombre del cliente *</Label>
              <Input
                id="author_name"
                value={form.author_name}
                onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                placeholder="Ej: María García"
              />
            </div>

            <div>
              <Label htmlFor="text">Testimonio *</Label>
              <Textarea
                id="text"
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                rows={4}
                placeholder="Lo que dijo el cliente sobre su experiencia..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="rating">Valoración</Label>
                <Select
                  value={String(form.rating)}
                  onValueChange={(val) => setForm({ ...form, rating: parseInt(val) })}
                >
                  <SelectTrigger id="rating">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 4, 3, 2, 1].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {'★'.repeat(n)}{'☆'.repeat(5 - n)} ({n})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="display_order">Orden</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={form.display_order}
                  onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="avatar_url">Foto (URL, opcional)</Label>
              <Input
                id="avatar_url"
                value={form.avatar_url}
                onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
                placeholder="https://..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Si se deja vacío, se muestran las iniciales del nombre.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
              <Label htmlFor="is_active">Visible en la Home</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
              {editingId ? 'Guardar Cambios' : 'Crear Testimonio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
